import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from '../Services/translation';
import { query, searchFacetChange } from '../Actions/FacetedSearch.action';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import Collapsible from './Collapsible';
import { FormControl, OutlinedInput } from '@material-ui/core';
import RangeSlider from './RangeSlider';
import Icon from './IconBase/Icon';

const FacetedSearchGroup = ({ group, initiallyOpened }) => {
  const dispatch = useDispatch();
  const { productsWrapper } = useSelector((state) => state.productsView);
  const [collapsed, setCollapsed] = useStateWithCallbackLazy(false);
  const showLessItemCount = useRef(5);
  const list = useRef(null);
  const showLess = useRef(null);
  const showMore = useRef(null);
  const listTop = useRef(null);
  const showLessBottom = useRef(null);
  const showMoreBottom = useRef(null);
  const firstRender = useRef(true);
  const [isOpen, setIsOpen] = useState(true);
  const [sliderValue, setSliderValue] = useState([]);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const label = (
    <div className="filter-group--label">
      {group.label}
      {group.selectedOptions.length > 0 ? (
        <span>({group.selectedOptions.length})</span>
      ) : (
        ''
      )}
    </div>
  );

  const { priceDisplayQuantity } = productsWrapper;
  const showingPricesForOne =
    !priceDisplayQuantity || priceDisplayQuantity == 1;

  const toggleShowMore = useCallback(() => {
    setCollapsed(
      (prevState) => !prevState,
      (collapsed) => {
        list.current.style.height = `${
          (collapsed ? showLessBottom.current : showMoreBottom.current) -
          listTop.current
        }px`;
      }
    );
  }, [setCollapsed]);

  useEffect(() => {
    // Make sure it is executed only once.
    // firstRender is used to prevent eslint warning when having empty dependencies array
    if (!firstRender.current) {
      return;
    }
    firstRender.current = false;

    if (list.current) {
      const {
        height: listHeight,
        top: listPosTop,
      } = list.current.getBoundingClientRect();

      listTop.current = listPosTop;
      if (showLess && showLess.currnent) {
        showLessBottom.current = showLess.current
          ? showLess.current.getBoundingClientRect().bottom
          : null;
      }
      if (showMore && showMore.current) {
        showMoreBottom.current = showMore.current.getBoundingClientRect().bottom;
      }

      const visible = listHeight !== 0;
      const tooMuchItem = group.options.length > showLessItemCount.current;
      visible && tooMuchItem && toggleShowMore();
    }
  }, [group, toggleShowMore]);

  useEffect(() => {
    calculatePriceSliderSetValues();
  }, [productsWrapper]);

  const calculatePriceSliderSetValues = () => {
    if (group.options && group.asSlider) {
      if (group.selectedOptions && group.selectedOptions.length > 0) {
        const ids = group.selectedOptions.map((object) => {
          return parseFloat(object);
        });
        var low = Math.min(...ids);
        var high = Math.max(...ids);

        if (isPriceFacet(group.id) && priceDisplayQuantity) {
          high = high * priceDisplayQuantity;
          low = low * priceDisplayQuantity;
        }

        UpdateSliderValues([low, high]);
      } else {
        const ids = group.options.map((object) => {
          return parseFloat(object.id);
        });
        let high = Math.max(...ids);
        let low = Math.min(...ids);

        if (isPriceFacet(group.id) && priceDisplayQuantity) {
          high = high * priceDisplayQuantity;
          low = low * priceDisplayQuantity;
        }
        UpdateSliderValues([low, high]);
      }
    }
  };

  const isPriceFacet = (id) => {
    return id == 'price_range';
  };

  const UpdateSliderValues = (value) => {
    setSliderValue(value);
  };

  const onSliderChange = useCallback(
    (group, value, quant) => {
      let valuesCopy = [...value];
      if (isPriceFacet(group.id)) {
        valuesCopy.forEach((v, index) => {
          valuesCopy[index] = Math.round(v / quant);
        });
      }
      dispatch(searchFacetChange(group, valuesCopy));
    },
    [dispatch]
  );

  const GetSliderValues = (sliderGroup) => {
    if (sliderGroup.options) {
      const ids = sliderGroup.options.map((object) => {
        return parseFloat(object.id);
      });
      let high = Math.max(...ids);
      let low = Math.min(...ids);

      //if the filtergroup is price, modify the values to correspond with the 1/4 setprice toggle value
      if (isPriceFacet(sliderGroup.id)) {
        if (!showingPricesForOne) {
          high = high * 4;
          low = low * 4;
        }
      }

      return (
        <RangeSlider
          value={sliderValue}
          step={1}
          unitOfMeasurement={sliderGroup.unitOfMeasurement}
          min={low}
          max={high}
          onChange={(_, newValue) =>
            UpdateSliderValues(newValue, !showingPricesForOne ? 4 : 1)
          }
          OnChangeCommitted={(_, newValue) =>
            onSliderChange(sliderGroup, newValue, !showingPricesForOne ? 4 : 1)
          }
        />
      );
    }
    return null;
  };

  return (
    <Collapsible
      initiallyOpen={initiallyOpened}
      label={label}
      className={'filter-group' + (isOpen ? ' filter-group--open' : '')}
      onStateChange={(open) => setIsOpen(open)}
    >
      {group.asSlider ? (
        <div className="faceted-search__group">{GetSliderValues(group)}</div>
      ) : (
        <>
          <FormControl
            className="faceted-search__filter-search-control"
            margin="dense"
          >
            <OutlinedInput
              margin="dense"
              id={label + '-filter-search'}
              className="faceted-search__filter-search"
              placeholder={translate('filter.search-filter')}
              autoComplete="off"
              value={localSearchQuery}
              onChange={(event) => setLocalSearchQuery(event.target.value)}
              endAdornment={
                <>
                  <Icon iconCode={'search'} />
                </>
              }
            />
          </FormControl>
          <ul className="faceted-search__group" ref={list}>
            {group.options &&
              group.options
                .filter(
                  (item) =>
                    localSearchQuery == '' ||
                    group.selectedOptions.includes(item.id) ||
                    item.label
                      .toLowerCase()
                      .includes(localSearchQuery.toLowerCase())
                )
                .sort((a, b) => (a.label > b.label ? 1 : -1))
                .map((item, itemIndex, array) => (
                  <li
                    key={`${item.label}-${itemIndex}`}
                    className="faceted-search__item"
                    role="faceted-search-item"
                    ref={(elem) => {
                      if (itemIndex === showLessItemCount.current - 1) {
                        showLess.current = elem;
                      }
                      if (itemIndex === array.length - 1) {
                        showMore.current = elem;
                      }
                    }}
                  >
                    <FacetedFilterCheckbox item={item} group={group} />
                  </li>
                ))}
          </ul>
        </>
      )}
      {!group.asSlider && group.options.length > showLessItemCount.current && (
        <span className="faceted-search__show-more" onClick={toggleShowMore}>
          {`${
            collapsed
              ? translate('filter.showmore')
              : translate('filter.showless')
          }`}
        </span>
      )}
    </Collapsible>
  );
};

const FacetedFilterCheckbox = ({ item, group }) => {
  const dispatch = useDispatch();
  const onChange = useCallback(
    (group, item) => dispatch(searchFacetChange(group, item)),
    [dispatch]
  );
  return (
    <label className="faceted-filter">
      <div className="faceted-filter__input--wrapper">
        <input
          className="faceted-filter__input"
          type="checkbox"
          onChange={(event) => onChange(group, item)}
          checked={
            group.selectedOptions != null &&
            group.selectedOptions.includes(item.id)
          }
        />
        <span className="faceted-filter__label">{item.label}</span>
      </div>
      {!isNaN(item.quantity) && item.quantity != null && (
        <span className="faceted-filter__quantity">
          &nbsp;({item.quantity})
        </span>
      )}
    </label>
  );
};

const FacetedSearch = (props) => {
  const dispatch = useDispatch();
  const { openedFacet } = props;
  const { facetFilters, navigationTheme } = useSelector(
    (state) => state.facetedSearch
  );

  useEffect(() => {
    // listen to history events (back, forward) of window
    window.onpopstate =
      window.onpopstate ||
      ((event) => {
        dispatch(query(window.location.search.substr(1) || ''));
      });
  }, [dispatch]);

  return (
    navigationTheme !== 'category' &&
    facetFilters &&
    facetFilters.length > 0 && (
      <ul className="faceted-search">
        {facetFilters &&
          facetFilters.map((group, groupIndex) => (
            <FacetedSearchGroup
              key={`${group.label}-${groupIndex}`}
              group={group}
              initiallyOpened={group.id == openedFacet}
            />
          ))}
      </ul>
    )
  );
};

export default FacetedSearch;
